"use client";

import { useMemo, useState, type FC } from "react";
import {
  Loader,
  Maximize,
  MessageCircleIcon,
  Minimize,
  XIcon,
} from "lucide-react";
import {
  LazyMotion,
  domMax,
  AnimatePresence,
  MotionConfig,
} from "motion/react";
import * as m from "motion/react-m";

import { ErrorBoundary } from "@/elements/components/assistant-ui/error-boundary";
import { Thread } from "@/elements/components/assistant-ui/thread";
import { ThreadList } from "@/elements/components/assistant-ui/thread-list";
import { useThemeProps } from "@/elements/hooks/useThemeProps";
import { useRadius } from "@/elements/hooks/useRadius";
import { useDensity } from "@/elements/hooks/useDensity";
import { assertNever, cn } from "@/lib/utils";
import { useElements } from "@/elements/hooks/useElements";
import { useExpanded } from "@/elements/hooks/useExpanded";
import { EASE_OUT_QUINT } from "@/elements/lib/easing";
import { useAuiState } from "@assistant-ui/react";

const LAYOUT_TRANSITION = {
  layout: {
    duration: 0.25,
    ease: EASE_OUT_QUINT,
  },
} as const;

type Dimensions = {
  width?: string | number;
  height?: string | number;
  maxHeight?: string | number;
};

interface AssistantModalProps {
  className?: string;
}

export const AssistantModal: FC<AssistantModalProps> = ({ className }) => {
  const { config } = useElements();
  const themeProps = useThemeProps();
  const r = useRadius();
  const d = useDensity();
  const [isOpen, setIsOpen] = useState(config.modal?.defaultOpen ?? false);
  const { expandable, isExpanded, setIsExpanded } = useExpanded();
  const title = config.modal?.title;
  const customIcon = config.modal?.icon;

  // Check if thread list should be shown
  const showThreadList =
    config.history?.enabled && config.history?.showThreadList !== false;

  const position = config.modal?.position ?? "bottom-right";
  const anchorPositionClass = positionClassname(position);

  const defaultDimensions: Dimensions = useMemo(
    () =>
      config.modal?.dimensions?.default ?? {
        width: "500px",
        height: "600px",
        maxHeight: "95vh",
      },
    [config.modal?.dimensions?.default],
  );

  const expandedDimensions: Dimensions = useMemo(
    () =>
      config.modal?.dimensions?.expanded ?? {
        width: "70vw",
        height: "90vh",
      },
    [config.modal?.dimensions?.expanded],
  );
  const isGenerating = useAuiState(({ thread }) =>
    thread.messages.some((message) => message.status?.type === "running"),
  );

  const effectiveWidth = isExpanded
    ? expandedDimensions.width
    : defaultDimensions.width;
  const effectiveHeight = isExpanded
    ? expandedDimensions.height
    : defaultDimensions.height;
  const effectiveMaxHeight = defaultDimensions.maxHeight;

  return (
    <LazyMotion features={domMax}>
      {/* reducedMotion="user" respects prefers-reduced-motion */}
      <MotionConfig reducedMotion="user" transition={LAYOUT_TRANSITION}>
        <div
          className={cn(
            "aui-root aui-modal-anchor fixed z-10",
            anchorPositionClass,
            themeProps.className,
            r("lg"),
            isOpen && "shadow-xl",
            className,
          )}
        >
          <AnimatePresence mode="wait">
            {!isOpen ? (
              <m.button
                key="button"
                layout
                layoutId="chat-container"
                onClick={() => setIsOpen(true)}
                className={cn(
                  "aui-modal-button flex size-12 cursor-pointer items-center justify-center border bg-primary text-primary-foreground shadow-lg transition-shadow hover:shadow-xl",
                  r("full"),
                )}
                initial={false}
                aria-label={`Open ${title}`}
                style={{ originX: 1, originY: 1 }}
              >
                <m.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.2, ease: EASE_OUT_QUINT }}
                  className="flex size-full items-center justify-center"
                >
                  {customIcon ? (
                    customIcon("closed")
                  ) : (
                    <MessageCircleIcon className="size-6" />
                  )}
                </m.div>
              </m.button>
            ) : (
              <m.div
                key="chat"
                layout
                layoutId="chat-container"
                className={cn(
                  // Descendant, not child: the thread root sits inside the attachment
                  // drop zone, so a direct-child selector would miss it and the
                  // modal would lose its popover background.
                  "aui-modal-content flex flex-col overflow-hidden border bg-popover text-popover-foreground [&_.aui-thread-root]:bg-inherit",
                  r("lg"),
                )}
                initial={false}
                style={{
                  originX: position.includes("left") ? 0 : 1,
                  originY: position.includes("top") ? 0 : 1,
                  width: effectiveWidth,
                  height: effectiveHeight,
                  maxHeight: effectiveMaxHeight,
                }}
              >
                <m.div
                  className={cn(
                    "aui-modal-header flex shrink-0 items-center justify-between border-b",
                    d("h-header"),
                    d("px-lg"),
                  )}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{
                    duration: 0.2,
                    delay: 0.1,
                    ease: EASE_OUT_QUINT,
                  }}
                >
                  <div className={cn("flex min-w-0 items-center")}>
                    <span
                      className={cn(
                        "text-md flex items-center gap-2 truncate font-medium text-foreground",
                        isGenerating && "title-shimmer",
                      )}
                    >
                      <span className="truncate">{title}</span>

                      {isGenerating && (
                        <Loader
                          className="size-4.5 animate-spin text-muted-foreground"
                          strokeWidth={1.25}
                        />
                      )}
                    </span>
                  </div>

                  <div className="flex flex-row items-center justify-end gap-1">
                    {expandable ? (
                      <button
                        type="button"
                        onClick={() => setIsExpanded((v) => !v)}
                        className={cn(
                          "flex h-8 cursor-pointer items-center rounded-md px-2 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground",
                        )}
                        aria-pressed={isExpanded}
                        aria-label={
                          isExpanded ? "Collapse assistant" : "Expand assistant"
                        }
                      >
                        {isExpanded ? (
                          <Minimize
                            strokeWidth={2}
                            className="size-3.5 rotate-90"
                          />
                        ) : (
                          <Maximize
                            strokeWidth={2}
                            className="size-3.5 rotate-90"
                          />
                        )}
                      </button>
                    ) : null}
                    <button
                      onClick={() => {
                        setIsOpen(false);
                        // Optional: reset expansion when closing
                        setIsExpanded(false);
                      }}
                      className="-mr-1 flex size-8 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                      aria-label={`Close ${title}`}
                    >
                      <XIcon className="size-4.5" />
                    </button>
                  </div>
                </m.div>

                {/* Main content area */}
                <m.div
                  className="aui-modal-body flex flex-1 overflow-hidden"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{
                    duration: 0.2,
                    delay: 0.05,
                    ease: EASE_OUT_QUINT,
                  }}
                >
                  {/* Thread list sidebar (when history enabled) */}
                  {showThreadList && (
                    <div className="aui-modal-thread-list w-56 shrink-0 overflow-y-auto border-r">
                      <ThreadList />
                    </div>
                  )}

                  {/* Thread content */}
                  <div className="aui-modal-thread w-full flex-1 overflow-hidden">
                    <ErrorBoundary>
                      <Thread />
                    </ErrorBoundary>
                  </div>
                </m.div>
              </m.div>
            )}
          </AnimatePresence>
        </div>
      </MotionConfig>
    </LazyMotion>
  );
};

function positionClassname(
  position:
    | "bottom-right"
    | "bottom-left"
    | "top-right"
    | "top-left"
    | undefined,
): string {
  switch (position) {
    case "bottom-left":
      return "left-4 bottom-4";
    case "top-right":
      return "right-4 top-4";
    case "top-left":
      return "left-4 top-4";
    case "bottom-right":
      return "right-4 bottom-4";
    case undefined:
      return "right-4 bottom-4";
    default:
      assertNever(position);
  }
}
